import { Link } from '@tanstack/react-router'
import { ArrowUpRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BlogPost } from '@/data/blog-posts'
import type { SupportedLocale } from '@/lib/i18n-utils'
import { formatPostDate } from '@/lib/format-date'
import {
  localizeBlogCategory,
  localizeBlogReadTime,
  localizeBlogTag,
} from '@/lib/blog-i18n'
import { usePublicCategories } from '@/hooks/useCategoryQueries'
import { usePublicTags } from '@/hooks/useTagQueries'
import { withLocalePath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'
import { IMAGE_WIDTHS, img, imgSrcSet } from '@/lib/image'

interface BlogPostCardProps {
  post: BlogPost
  locale: SupportedLocale
  className?: string
  compact?: boolean
}

export function BlogPostCard({
  post,
  locale,
  className,
  compact = false,
}: BlogPostCardProps) {
  const { t } = useTranslation()
  const { data: categories = [] } = usePublicCategories()
  const { data: tags = [] } = usePublicTags()
  const category = localizeBlogCategory(categories, post.category, locale)
  const displayTags = post.tags.slice(0, 2)

  return (
    <Link
      to={withLocalePath(`/blog/${post.slug}`, locale)}
      className={cn('journal-blog-card group block h-full', className)}
    >
      <article
        className={cn(
          'journal-blog-card-inner',
          compact && 'journal-blog-card--compact',
        )}
      >
        <div className="journal-blog-card-image">
          {post.coverImage ? (
            <img
              src={img(post.coverImage, { width: 800, format: 'auto' })}
              srcSet={imgSrcSet(post.coverImage, IMAGE_WIDTHS.cover)}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              alt=""
              width={680}
              height={383}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="journal-blog-card-placeholder">{category}</span>
          )}
        </div>
        <div className="journal-blog-card-content">
          <div className="journal-blog-card-meta">
            <span>{category}</span>
            <time dateTime={post.publishedAt}>
              {formatPostDate(post.publishedAt, locale, 'short')}
            </time>
            <span>{localizeBlogReadTime(t, post.readTime)}</span>
          </div>
          <h3>{post.title}</h3>
          {!compact && <p>{post.excerpt}</p>}
          {displayTags.length > 0 && !compact && (
            <div className="journal-blog-card-tags">
              {displayTags.map((tagSlug) => (
                <span key={tagSlug}>
                  {localizeBlogTag(tags, tagSlug, locale)}
                </span>
              ))}
            </div>
          )}
          <div className="journal-blog-card-footer">
            <span>{post.author}</span>
            <ArrowUpRight size={20} aria-hidden="true" />
          </div>
        </div>
      </article>
    </Link>
  )
}
